import React, { useMemo } from "react";
import SVGIcon, { SVGIconName } from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view/view";
import { gettext } from "web/script/modules/django-i18n";
import { formatDateMY } from "web/script/utils/dates";
import * as styles from "./storefront-info-list.css";

interface InfoItemProps {
    iconName: SVGIconName;
    text: string;
    key: string;
}

function InfoItem({ iconName, text, key }: InfoItemProps): React.ReactElement {
    return (
        <HStack spacing="xxs" align="center" key={key}>
            <SVGIcon className={styles.moreInfoIcon} name={iconName} />
            <Text textStyle="body-s">{text}</Text>
        </HStack>
    );
}

interface StorefrontInfoListProps {
    dateAdded?: string | null;
    shipsToCountry?: string | null;
    bestShippingOption?: string | null;
}

export function StorefrontInfoList({
    dateAdded,
    shipsToCountry,
    bestShippingOption,
}: StorefrontInfoListProps): React.ReactElement {
    const items = useMemo(() => {
        const items: InfoItemProps[] = [];

        if (shipsToCountry) {
            items.push({
                iconName: "world",
                text: gettext("storefront.shipping_info.country", { country: shipsToCountry }),
                key: "ships-to",
            });
        }

        if (bestShippingOption) {
            items.push({
                iconName: "local-shipping-sharp",
                text: gettext("storefront.shipping_info.days", { days: bestShippingOption }),
                key: "shipping",
            });
        }

        if (dateAdded) {
            items.push({
                iconName: "time-sharp",
                text: gettext("storefront.joined_date", { date: formatDateMY(dateAdded) }),
                key: "date-added",
            });
        }

        return items;
    }, [dateAdded, bestShippingOption, shipsToCountry]);

    return (
        <>
            {items.length > 0 && (
                <View className={styles.container}>
                    <Text textStyle="body-bold">{gettext("general.more_information")}</Text>
                    <View marginTop="xs">
                        <VStack spacing="xxs">
                            {items.map(({ key, iconName, text }) => (
                                <InfoItem key={key} iconName={iconName} text={text} />
                            ))}
                        </VStack>
                    </View>
                </View>
            )}
        </>
    );
}
